import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

export type PermissionMatrix = Record<
  string,
  Record<'view' | 'create' | 'edit' | 'delete', boolean>
>;

const initialState: PermissionMatrix = {
  Campaigns: { view: false, create: false, edit: false, delete: false },
  Automations: { view: false, create: false, edit: false, delete: false },
  Templates: { view: false, create: false, edit: false, delete: false },
};

// Maps role permissions from roleMatrix to a PermissionMatrix for a specific role
export const mapRoleToPermissions = (
  role: string,
  roleMatrix: Record<string, Record<string, boolean>>
): PermissionMatrix => {
  const result: PermissionMatrix = JSON.parse(JSON.stringify(initialState));

  Object.entries(roleMatrix).forEach(([permKey, roleValues]) => {
    if (!roleValues[role]) return;

    if (permKey.includes('Template')) {
      if (permKey.startsWith('view')) result.Templates.view = true;
      if (permKey.startsWith('create')) result.Templates.create = true;
      if (permKey.startsWith('update') || permKey.startsWith('duplicate'))
        result.Templates.edit = true;
      if (permKey.startsWith('delete')) result.Templates.delete = true;
    }

    if (permKey.includes('Campaign')) {
      result.Campaigns.view = true;
      if (permKey.startsWith('create')) result.Campaigns.create = true;
      if (permKey.startsWith('edit')) result.Campaigns.edit = true;
    }

    if (permKey.includes('Automation')) {
      result.Automations.view = true;
      if (permKey.startsWith('create')) result.Automations.create = true;
      if (permKey.startsWith('edit')) result.Automations.edit = true;
    }
  });

  return result;
};

// Maps PermissionMatrix back to roleMatrix for saving role permissions
export const mapPermissionsToRoles = (
  role: string,
  matrix: PermissionMatrix,
  roleMatrix: Record<string, Record<string, boolean>>
): Record<string, Record<string, boolean>> => {
  const updated: Record<string, Record<string, boolean>> = JSON.parse(
    JSON.stringify(roleMatrix)
  );

  Object.entries(matrix).forEach(([module, perms]) => {
    if (module === 'Templates') {
      updated.viewTemplates[role] = perms.view;
      updated.createTemplates[role] = perms.create;
      updated.updateTemplates[role] = perms.edit;
      updated.duplicateTemplates[role] = perms.edit;
      updated.deleteTemplates[role] = perms.delete;
    }
    if (module === 'Campaigns') {
      updated.createCampaign[role] = perms.create;
      updated.editCampaign[role] = perms.edit;
    }
    if (module === 'Automations') {
      updated.createAutomation[role] = perms.create;
      updated.editAutomation[role] = perms.edit;
    }
  });

  return updated;
};

const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    // Saves updated permissions to the Redux store
    savePermissions: (_, action: PayloadAction<PermissionMatrix>) => {
      return action.payload;
    },
    // Resets permissions to the initial default state
    resetPermissions: () => initialState,
  },
});

export const { savePermissions, resetPermissions } = memberSlice.actions;
export const selectMemberPermissions = (state: RootState) => state.member;
export default memberSlice.reducer;
